/**
 * Centralised display formatting for the reports product.
 *
 * Two responsibilities:
 *
 * 1. `humanizeMeasure(name)` / `humanizeDimension(name)` — turn a registry
 *    measure / dimension name into the label a human should see on a chart.
 *    "spec_open_rate" → "Open rate", "recruiter_name" → "Recruiter",
 *    "event_at" → "Time".
 *
 * 2. `formatValue(n, format)` — turn a raw numeric value into a string with
 *    the format hint (number, compact, percent, currency, duration_*) applied
 *    via Intl.NumberFormat. Used by every chart, table cell, and KPI card.
 *
 * Single source of truth. Don't sprinkle ad-hoc String(value) or .toFixed()
 * anywhere — call formatValue. Don't print raw measure names in axes or
 * tooltips — call humanizeMeasure.
 */
import type { NumberFormat } from "./schema";

export type { NumberFormat } from "./schema";

const MEASURE_OVERRIDES: Record<string, string> = {
  // Float funnel — a "float" is a candidate presentation packet a recruiter
  // shares with clients. Float emails carry the candidate selection.
  floats_sent: "Floats sent",
  float_opens: "Opens",
  float_replies: "Replies",
  float_open_rate: "Open rate",
  float_reply_rate: "Reply rate",
  float_meeting_rate: "Meeting rate",
  meetings_booked: "Meetings booked",
  // Legacy aliases — older reports might still reference these. Keep them
  // mapped to the same human labels so the chart doesn't show a raw key.
  spec_cvs_sent: "Floats sent",
  spec_opens: "Opens",
  spec_replies: "Replies",
  spec_meetings: "Meetings booked",
  spec_open_rate: "Open rate",
  spec_reply_rate: "Reply rate",
  spec_meeting_rate: "Meeting rate",
  // Activity
  emails_drafted: "Emails drafted",
  follow_ups_completed: "Follow-ups completed",
  profiles_generated: "Profiles generated",
  profiles_created: "Profiles created",
  profiles_shared: "Profiles shared",
  profile_open_rate: "Profile open rate",
  candidates_presented: "Candidates presented",
  candidate_count: "Candidates",
  // Pipeline
  open_jobs: "Open jobs",
  placement_velocity_days: "Placement velocity (days)",
  shortlist_to_placement_rate: "Shortlist → placement rate",
  // Intent
  client_intent_score: "Client intent",
  // Quality
  missing_salary_pct: "Missing salary %",
  missing_location_pct: "Missing location %",
  stale_candidates_pct: "Stale candidates %",
  duplicate_candidates_count: "Duplicate candidates",
  // Productivity
  avg_response_time_minutes: "Avg response (min)",
  roles_worked: "Roles worked",
  meeting_no_show_rate: "No-show rate",
};

const DIMENSION_OVERRIDES: Record<string, string> = {
  recruiter_name: "Recruiter",
  recruiter_id: "Recruiter",
  client_name: "Client",
  client_id: "Client",
  candidate_id: "Candidate",
  candidate_name: "Candidate",
  job_id: "Job",
  campaign_id: "Campaign",
  channel: "Channel",
  ats_source: "Source",
  team_id: "Team",
  stage: "Stage",
  day_of_week: "Day of week",
  hour_of_day: "Hour of day",
  interaction_type: "Interaction",
  send_state: "Send state",
  recruiter_company: "Recruiter company",
  event_at: "Time",
  month: "Month",
  week: "Week",
};

/**
 * Convert an arbitrary snake_case / camelCase / Cube-namespaced
 * identifier into Title Case for display when no explicit override exists.
 * Strips a leading Cube namespace ("SentEmail.openRate" → "Open rate") and
 * splits on _ and case transitions.
 */
function titleCase(raw: string): string {
  if (!raw) return ""; // null-safe: a missing measure/dimension renders as a blank label, never a crash
  const last = raw.includes(".") ? raw.split(".").pop()! : raw;
  const spaced = last
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Title-case every word of a snake_case / dotted identifier — e.g.
 * "spec_open_rate" → "Spec Open Rate", "Contact.source" → "Contact Source".
 *
 * Distinct from `titleCase` (which sentence-cases and strips the Cube
 * namespace). This is the "column header / mock label" casing used by the
 * spec-builder, the mock adapter, and the table editor, where the user
 * expects each word capitalised. Splits on `.` and `_`.
 */
export function titleCaseWords(raw: string): string {
  return raw
    .split(/[._]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function humanizeMeasure(name: string): string {
  return MEASURE_OVERRIDES[name] ?? titleCase(name);
}

export function humanizeDimension(name: string): string {
  return DIMENSION_OVERRIDES[name] ?? titleCase(name);
}

/**
 * Fallback to humanizeMeasure if humanizeDimension misses — keeps charts
 * legible even when an encoding column is a measure (e.g. heatmap colour).
 */
export function humanizeAny(name: string): string {
  return MEASURE_OVERRIDES[name] ?? DIMENSION_OVERRIDES[name] ?? titleCase(name);
}

/**
 * Format a numeric value for display. Always returns a string.
 * Handles null/undefined / NaN with an em-dash. Uses en-US grouping so
 * 13329 → "13,329", not "13.329" (locale gotcha that has bitten us).
 */
export function formatValue(
  value: unknown,
  format: NumberFormat | undefined = "number",
): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";

  switch (format) {
    case "percent": {
      // 0.34 → "34%", 34 → "34%" if it's already a whole percent.
      const pct = Math.abs(n) <= 1 ? n * 100 : n;
      return PERCENT_FMT.format(pct / 100);
    }
    case "currency":
      return CURRENCY_FMT.format(n);
    case "compact":
      return COMPACT_FMT.format(n);
    case "duration_minutes":
      return `${INT_FMT.format(Math.round(n))} min`;
    case "duration_seconds":
      return `${INT_FMT.format(Math.round(n))}s`;
    case "number":
    default:
      // Integer if rounded close to integer, else 1 decimal.
      return Math.abs(n - Math.round(n)) < 1e-6
        ? INT_FMT.format(Math.round(n))
        : DEC_FMT.format(n);
  }
}

const INT_FMT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const DEC_FMT = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});
const PERCENT_FMT = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});
const CURRENCY_FMT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const COMPACT_FMT = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

/**
 * Resolve a NumberFormat for a measure by looking up the metric registry
 * column metadata that travelled with the QueryResult. The registry is the
 * single source of truth for "is this a rate / currency / duration"; we do
 * not infer from name suffixes (that was a maintenance trap — adding a new
 * measure required updating a hidden string-match table).
 *
 * If the column doesn't carry a format (e.g. a raw Cube response that the
 * adapter didn't enrich), we fall back to plain number.
 */
export function formatFromColumn(
  measure: string,
  columns: Array<{ key: string; format?: NumberFormat }> | undefined,
): NumberFormat {
  return columns?.find((c) => c.key === measure)?.format ?? "number";
}
